import { Link } from 'react-router';
import { fmtTime } from '../../lib/format';
import { LEVEL_LABEL, type ItemType, type Level } from '../../lib/library';
import { TypeBadge } from './parts';

/** Floor feed card for new library items and new strategy versions (INT-FLR-01). The API only sends these to the item's audience. */

export interface LibraryFeedEntry {
  id: string;
  type: string;
  createdAt: string;
  actor: { id: string; name: string };
  market: { id: string; code: string } | null;
  libraryItem: {
    id: string;
    type: ItemType;
    title: string;
    excerpt: string | null;
    tags: string[];
    level: Level | null;
    strategy: { code: string; name: string } | null;
    file: { filename: string; sizeBytes: number } | null;
    version: { version: number; changelog: string | null } | null;
  };
}

export const isLibraryFeed = (i: object): i is LibraryFeedEntry => 'libraryItem' in i && !!(i as { libraryItem?: unknown }).libraryItem;

const initials = (n: string) => n.split(' ').map((p) => p[0]).join('').slice(0, 2);
const WHAT: Record<ItemType, string> = { VIDEO_LINK: 'a video', DOCUMENT: 'a document', STRATEGY: 'a strategy', RECORDING: 'a recording' };

export function LibraryFeedItem({ item: i, tz }: { item: LibraryFeedEntry; tz: string }) {
  const li = i.libraryItem;
  const versioned = i.type === 'STRATEGY_VERSIONED';
  const link = li.strategy ? `/library/strategies/${encodeURIComponent(li.strategy.code)}` : `/library/items/${li.id}`;
  return (
    <article className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2a2f3a] text-xs font-semibold">{initials(i.actor.name)}</div>
          <div className="flex flex-col">
            <span>
              <strong>{i.actor.name}</strong>{' '}
              <span className="text-sub">{versioned ? `updated strategy ${li.strategy?.code} to v${li.version?.version}` : `added ${WHAT[li.type]} to the library`}</span>
            </span>
            <span className="text-xs text-muted">
              {fmtTime(i.createdAt, tz, true)} {i.market && `· ${i.market.code}`} {li.level && `· ${LEVEL_LABEL[li.level]}`}
            </span>
          </div>
        </div>
        <TypeBadge type={li.type} className="self-start" />
      </div>
      <Link to={link} className="text-base font-semibold text-ink hover:text-accent">
        {li.strategy ? `${li.strategy.code} · ` : ''}
        {li.title}
      </Link>
      {versioned && li.version?.changelog && <p className="m-0 border-l-2 border-[#1e5a3c] pl-3 leading-relaxed text-ink2">{li.version.changelog}</p>}
      {!versioned && li.excerpt && <p className="m-0 leading-relaxed text-ink2">{li.excerpt}</p>}
      {li.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {li.tags.map((t) => (
            <span key={t} className="rounded-full border border-line2 bg-raised px-2.5 py-0.5 text-xs text-sub">
              #{t}
            </span>
          ))}
        </div>
      )}
      <Link to={link} className="self-start text-sm">
        {versioned ? 'See what changed →' : li.type === 'VIDEO_LINK' ? 'Watch →' : 'Open →'}
      </Link>
    </article>
  );
}
